"use client";

import { useSetAtom } from "jotai";
import type { MapLayerMouseEvent, SymbolLayerSpecification } from "maplibre-gl";
import { RLayer, RSource, useMap } from "maplibre-react-components";
import React, { type ReactElement } from "react";
import { useSites } from "src/hooks/sites";
import { selectedSiteAtom } from "@/store/sites";
import { ThemeTypeEnum, type Site } from "@/types";
import { getThemeColor } from "@/utils";

interface SitesMapLayerPointProps {
  sites: Site[];
  theme: ThemeTypeEnum;
  interactive: boolean;
}

/**
 * SitesMapLayerPoint
 * @param sites
 * @param theme
 * @constructor
 */
function SitesMapLayerPoint({
  sites,
  theme,
  interactive = true,
}: Readonly<SitesMapLayerPointProps>): ReactElement {
  const map = useMap();
  const { getSitesPointFeatureCollection } = useSites(sites);
  const setSelectedSite = useSetAtom(selectedSiteAtom);

  const features = getSitesPointFeatureCollection(sites);
  const color = getThemeColor(theme);
  const source = `source-point-${theme}`;
  const layer = `layer-point-${theme}`;
  const icon = `circle-${color}`;
  const layout: SymbolLayerSpecification["layout"] = {
    "icon-image": icon,
    "icon-size": [
      "interpolate",
      ["linear"],
      ["zoom"],
      3,
      0.125,
      10,
      0.25,
      16,
      0.5,
    ],
    "icon-allow-overlap": true,
  };

  const handleClick = (e: MapLayerMouseEvent): void => {
    if (e.features?.[0].properties) {
      const newSite = sites.find((site) => {
        return site.id === e.features?.[0].properties.id;
      });
      if (newSite) setSelectedSite(newSite);
    }
  };

  const handleMouseEnter = (): void => {
    map.getCanvas().style.cursor = "pointer";
  };

  const handleMouseLeave = (): void => {
    map.getCanvas().style.cursor = "";
  };

  return (
    <>
      <RSource
        data={features}
        generateId
        id={source}
        key={source}
        type="geojson"
      />
      <RLayer
        id={layer}
        key={layer}
        onClick={interactive ? handleClick : undefined}
        onMouseEnter={interactive ? handleMouseEnter : undefined}
        onMouseLeave={interactive ? handleMouseLeave : undefined}
        source={source}
        type="symbol"
        layout={layout}
      />
    </>
  );
}

export { SitesMapLayerPoint, type SitesMapLayerPointProps };
